import { useState } from "react";
import { NavLink, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useReadyAuth } from "../auth/use-auth";
import { useTheme } from "../theme/use-theme";
import { useTriageCounts } from "@/features/signals/hooks";
import { useContacts } from "@/features/contacts/hooks";
import { freshnessStamp } from "@/features/signals/format";
import bdseLogoMark from "../assets/bdse-logo-mark.png";
import ankuraLogo from "../assets/ankura-logo-2021-rgb.webp";
import { useRadarStatus } from "@/features/radar/hooks";

/**
 * AppShell — the authenticated platform shell, rebuilt to the sealed signals
 * dashboard redesign (Reskin Session A). Shared across every view: the nav is
 * in scope for this slice; the other views keep their existing `.bdse-*` content
 * styling and render into `.sx-main` until their own reskin sessions.
 *
 * Redesign nav (sealed mock, Addendum Section 6): logo block + "Last update"
 * stamp, search, a "Views" eyebrow, the Views nav items (badges only where a
 * live count is wired), a flex spacer, and the account card pinned to the
 * bottom. The nav is
 * viewport-sticky so the account card stays visible bottom-left as the feed
 * scrolls.
 *
 * Behavior retained from Phase C/D + 7.1c:
 *   - Signals, Contacts and Company Search are the clickable product views.
 *     Intel to Influence, RFP Pursuits and Attention Objects remain visible as
 *     inert Coming Soon rows. Magic Links management lives in Settings.
 *     Hidden routes stay in AppRouter/src/pages (hide, not delete).
 *   - Live badge counts: Signals from the feed triage total, Contacts from the
 *     viewer's roster. Sources without a wired count hide the badge (null)
 *     rather than render a misleading 0.
 *   - The Dark toggle is wired to useTheme(); the account-card "Dark"/"Light"
 *     label flips. The reskinned surfaces are light-only (the sealed mock has no
 *     dark variant), so toggling flips the app body but not the reskin.
 *   - Log out and Settings are unchanged.
 *
 * The logo is the sealed transparent mark shipped verbatim from the design
 * reference (renders "BD" in blue; never redrawn or recoloured). It is used in
 * both themes because the reskin nav is light-only.
 */
export function AppShell() {
  const { signOut } = useAuth();
  const { profile } = useReadyAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  // The nav "Search signals" box is URL-backed (?q=), mirroring how the feed's
  // tab/type filters travel, so SignalsPage can read the term and filter the
  // visible feed. Written with replace so keystrokes don't spam history.
  const [params, setParams] = useSearchParams();
  const search = params.get("q") ?? "";
  const setSearch = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("q", value);
    else next.delete("q");
    setParams(next, { replace: true });
  };

  // Live nav badge counts. Signals total from the feed's triage summary;
  // Contacts from the viewer's own watchlist. Both resolve through the
  // config-selected adapter (mock or live). Until each query settles the badge
  // is hidden (null) rather than flashing a wrong 0.
  const triage = useTriageCounts(profile.id);
  const contacts = useContacts("mine", { autoLoadAll: false });
  const radar = useRadarStatus();
  const signalsCount = triage.data?.total ?? null;
  const contactsCount = contacts.total ?? null;
  const showRadar = Boolean(
    radar.data &&
    (radar.data.enabled || radar.data.pursuits.length > 0 || radar.data.trackers.length > 0)
  );
  // Show when the last completed sweep finished. Hidden until the triage query
  // settles; "None on record" when the timestamp is null.
  const lastSweepStamp = triage.data ? freshnessStamp(triage.data.latestSweepFinishedAt) : null;

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  // Production nav — the locked target shape. `hidden` entries keep their routes
  // (AppRouter) and pages (src/pages); hiding them from the nav is reversible.
  // Do NOT delete them.
  const viewsNav: NavItemDef[] = [
    { to: "/signals", label: "Signals", unit: "signals", count: signalsCount },
    { to: "/contacts", label: "Contacts", unit: "contacts", count: contactsCount },
    // Build placeholder (shell only, no feature logic). No count source and no
    // badge — a badge is a live-data claim this surface cannot make yet.
    { to: "/company-search", label: "Company Search", unit: "", count: null },
    ...(showRadar
      ? [
          {
            to: "/radar",
            label: "RADAR",
            unit: "pursuits",
            count: radar.data?.pursuits.length ?? 0,
          },
        ]
      : []),
    // Hidden from production nav (hide, do NOT delete):
    { to: "/archive", label: "Archive", unit: "signals", count: 0, hidden: true },
    { to: "/home", label: "Home", unit: "", count: null, hidden: true },
    { to: "/pursuits", label: "Pursuit Tracker", unit: "", count: 0, hidden: true },
  ];

  const comingSoonNav: { label: string }[] = [
    { label: "Intel to Influence" },
    { label: "RFP Pursuits" },
    { label: "Attention Objects" },
  ];

  const roleLabel =
    profile.role === "super_admin" ? "SUPER ADMIN" : profile.role === "admin" ? "ADMIN" : "USER";

  return (
    <div className="sx-app">
      <nav className="sx-nav" aria-label="Views">
        <div className="sx-logo-block">
          <img className="sx-bdse-logo" src={bdseLogoMark} alt="BD Signal Engine" />
          <div className="sx-by-ankura" aria-label="by Ankura">
            <span aria-hidden="true">by</span>
            <img src={ankuraLogo} alt="Ankura" />
          </div>
          {lastSweepStamp ? <p className="sx-logo-stamp">● Last sweep: {lastSweepStamp}</p> : null}
        </div>

        <div className="sx-nav-search">
          <label className="sx-sr-only" htmlFor="feed-search">
            Search signals
          </label>
          <input
            id="feed-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search signals..."
          />
          {search && (
            <button
              className="sx-clear"
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <p className="sx-nav-eyebrow">Views</p>
        {viewsNav
          .filter((item) => !item.hidden)
          .map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        <button
          type="button"
          className="sx-coming-soon-toggle"
          aria-expanded={comingSoonOpen}
          aria-controls="coming-soon-views"
          onClick={() => setComingSoonOpen((open) => !open)}
        >
          <span>Coming soon</span>
          <span className="sx-coming-soon-count">{comingSoonNav.length}</span>
          <span
            className={`sx-coming-soon-chevron${comingSoonOpen ? " sx-coming-soon-chevron--open" : ""}`}
            aria-hidden="true"
          >
            ▾
          </span>
        </button>
        {comingSoonOpen ? (
          <div id="coming-soon-views" className="sx-coming-soon-list">
            {comingSoonNav.map((item) => (
              <ComingSoonNavItem key={item.label} label={item.label} />
            ))}
          </div>
        ) : null}

        <div className="sx-nav-spacer" />

        <div className="sx-acct">
          <p className="sx-email" title={profile.email}>
            {profile.email}
          </p>
          <div className="sx-acct-row">
            <button
              type="button"
              className="sx-ub"
              onClick={toggleTheme}
              title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            <NavLink to="/settings" className="sx-ub">
              Settings
            </NavLink>
            <button type="button" className="sx-ub" onClick={handleSignOut}>
              Log out
            </button>
          </div>
          <span className="sx-op-badge">{roleLabel}</span>
        </div>
      </nav>

      <main className="sx-main">
        <Outlet />
      </main>
    </div>
  );
}

type NavItemDef = {
  to: string;
  label: string;
  /** Hidden accessible unit so the badge reads "Signals, 894 signals". */
  unit: string;
  count: number | null;
  /** Kept for reference but not rendered when true (hide, not delete). */
  hidden?: boolean;
};

function NavItem({ item }: { item: NavItemDef }) {
  return (
    <NavLink to={item.to} end className="sx-navitem">
      {({ isActive }) => (
        <>
          <span className="sx-navitem-label">
            {isActive ? <span className="sx-dot" /> : null}
            {item.label}
          </span>
          {item.count !== null && (
            <span className="sx-navct">
              {item.count.toLocaleString()}
              {item.unit ? <span className="sx-sr-only"> {item.unit}</span> : null}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function ComingSoonNavItem({ label }: { label: string }): JSX.Element {
  return (
    <button type="button" className="sx-navitem sx-navitem--disabled" disabled>
      <span className="sx-navitem-label">{label}</span>
      <span className="sx-navct sx-navct--soon">Coming Soon</span>
    </button>
  );
}
